import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Icon,
  List,
  Progress,
  NativeSelect,
  Spinner,
  Text,
  VStack,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import {
  FaCheckCircle,
  FaCloudUploadAlt,
  FaExclamationTriangle,
  FaFileAlt,
  FaObjectGroup,
  FaRobot,
  FaTrash,
} from 'react-icons/fa';
import { bandasAPI } from '../services/api';
import { motivoRevisionOrientacion } from '../utils/partituraOrientacion';

const CONF_UMBRAL_ETIQUETAR = 0.9;

function normalizeTitle(t) {
  if (!t) return '';
  return t.toLowerCase().replace(/[^\w\sáéíóúñüà-ÿ]/g, '').replace(/\s+/g, ' ').trim();
}

function titlesMatch(a, b) {
  const na = normalizeTitle(a);
  const nb = normalizeTitle(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.length > 3 && nb.length > 3 && (na.includes(nb) || nb.includes(na))) return true;
  return false;
}

function findGroupByTitle(groups, titulo) {
  if (!titulo) return null;
  return groups.find((g) => titlesMatch(g.titulo, titulo)) || null;
}

/**
 * Agrupa páginas en obras usando:
 * 1. archivoOrigen: un cambio de archivo activa un modo preámbulo persistente
 *    que buferea TODAS las páginas no-instrumentales hasta encontrar la primera
 *    página con contenido instrumental, momento en que se crea/asigna grupo.
 * 2. tipoPagina: portadas, guiones y páginas sin instrumento son preámbulo
 *    que se adjunta a las partes que siguen.
 * 3. tituloObra: matching flexible para unir páginas de la misma obra.
 */
function groupSugerenciasByObra(sugerencias) {
  if (!sugerencias.length) return [];

  const groups = [];
  let preamble = [];
  let currentGroup = null;
  let lastFile = null;
  let inFilePreamble = true;

  function isContentPage(s) {
    const t = s.tipoPagina || 'otro';
    return t === 'instrumento' || t === 'letra';
  }

  function attachToGroupOrCreate(s) {
    const titulo = s.tituloObra?.trim() || '';
    const preambleTitulo = preamble.find((p) => p.tituloObra?.trim())?.tituloObra?.trim() || '';
    const searchTitle = titulo || preambleTitulo;

    if (searchTitle) {
      const existing = findGroupByTitle(groups, searchTitle);
      if (existing) {
        if (preamble.length) { existing.paginas.push(...preamble); preamble = []; }
        existing.paginas.push(s);
        currentGroup = existing;
        return;
      }
    }

    const bestTitulo = titulo
      || (preambleTitulo && !titulo ? preambleTitulo : '')
      || 'Sin título';
    const newG = { titulo: bestTitulo, autor: '', paginas: [...preamble, s] };
    preamble = [];
    groups.push(newG);
    currentGroup = newG;
  }

  for (const s of sugerencias) {
    const tipo = s.tipoPagina || 'otro';
    const titulo = s.tituloObra?.trim() || '';
    const archivo = s.archivoOrigen || null;
    const fileChanged = archivo && lastFile && archivo !== lastFile;
    lastFile = archivo || lastFile;

    if (fileChanged) {
      if (preamble.length && currentGroup) {
        currentGroup.paginas.push(...preamble);
        preamble = [];
      }
      inFilePreamble = true;
    }

    if (inFilePreamble) {
      if (isContentPage(s)) {
        inFilePreamble = false;
        attachToGroupOrCreate(s);
      } else {
        preamble.push(s);
      }
      continue;
    }

    if (s.paginaContinuacion && currentGroup) {
      currentGroup.paginas.push(s);
      continue;
    }

    if (tipo === 'portada' || tipo === 'guion') {
      if (currentGroup) {
        currentGroup.paginas.push(s);
      } else {
        preamble.push(s);
      }
      continue;
    }

    if (titulo) {
      const existing = findGroupByTitle(groups, titulo);
      if (existing) {
        if (preamble.length) { existing.paginas.push(...preamble); preamble = []; }
        existing.paginas.push(s);
        currentGroup = existing;
      } else {
        attachToGroupOrCreate(s);
      }
    } else {
      if (currentGroup) {
        currentGroup.paginas.push(s);
      } else {
        preamble.push(s);
      }
    }
  }

  if (preamble.length) {
    if (currentGroup) {
      currentGroup.paginas.push(...preamble);
    } else {
      const portada = preamble.find((p) => p.tipoPagina === 'portada' && p.tituloObra?.trim());
      groups.push({
        titulo: portada?.tituloObra?.trim() || 'Sin título',
        autor: '',
        paginas: preamble,
      });
    }
  }

  for (const g of groups) {
    if (!g.titulo || g.titulo === 'Sin título') {
      const portada = g.paginas.find((p) => p.tipoPagina === 'portada' && p.tituloObra?.trim());
      const guion = g.paginas.find((p) => p.tipoPagina === 'guion' && p.tituloObra?.trim());
      const any = g.paginas.find((p) => p.tituloObra?.trim());
      g.titulo = portada?.tituloObra?.trim() || guion?.tituloObra?.trim() || any?.tituloObra?.trim() || 'Sin título';
    }
    if (!g.autor) {
      const portada = g.paginas.find((p) => p.tipoPagina === 'portada' && p.autor?.trim());
      g.autor = portada?.autor?.trim() || g.paginas.find((p) => p.autor?.trim())?.autor?.trim() || '';
    }
    g.paginas.sort((a, b) => (a.orden || 0) - (b.orden || 0));
  }

  return groups.filter((g) => g.paginas.length > 0);
}

/** Une varias obras del resumen en una. Si `keepLabels` es false, limpia instrumentos/voces. */
function mergeObrasGroups(obras, selectedIndices, primaryIndex, keepLabels) {
  const indices = [...selectedIndices].sort((a, b) => a - b);
  if (indices.length < 2) return obras;
  const primary = obras[primaryIndex] || obras[indices[0]];
  let paginas = [];
  for (const i of indices) {
    paginas.push(...(obras[i]?.paginas || []));
  }
  paginas.sort((a, b) => (a.orden || 0) - (b.orden || 0));
  if (!keepLabels) {
    paginas = paginas.map((p) => ({
      ...p,
      instrumentosVoces: [],
      confianza: null,
    }));
  }
  const merged = {
    titulo: primary?.titulo || 'Sin título',
    autor: primary?.autor || '',
    paginas,
  };
  const next = [];
  let inserted = false;
  for (let i = 0; i < obras.length; i++) {
    if (indices.includes(i)) {
      if (!inserted) {
        next.push(merged);
        inserted = true;
      }
    } else {
      next.push(obras[i]);
    }
  }
  return next;
}

/**
 * Wizard multi-obra: archivos → análisis IA → resumen → géneros → avisos → creación.
 */
export default function BandaRepertorioAltaObraWizard({
  isOpen,
  generosCatalog,
  onClose,
  onComplete,
  detectarNuevosMaestros,
  crearMaestrosPendientes,
}) {
  
  const pollingRef = useRef(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const uploadSessionRef = useRef({ key: '', obraId: null, inFlight: false });

  const [step, setStep] = useState('upload');
  const [errorMsg, setErrorMsg] = useState('');
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState('');
  const [obraId, setObraId] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobEstado, setJobEstado] = useState(null);
  const [sugerencias, setSugerencias] = useState([]);
  const [obras, setObras] = useState([]);
  const [generosPorObra, setGenerosPorObra] = useState({});
  const [finalizando, setFinalizando] = useState(false);
  const [resultadoFinal, setResultadoFinal] = useState(null);
  const [mergeSel, setMergeSel] = useState([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeKeepLabels, setMergeKeepLabels] = useState(true);
  const [mergePrimaryIdx, setMergePrimaryIdx] = useState(0);

  const dropZoneBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const dropZoneBorder = useColorModeValue('gray.300', 'whiteAlpha.300');
  const dropZoneHoverBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const fileRowHoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');

  const resetState = useCallback(() => {
    setStep('upload');
    setErrorMsg('');
    setFiles([]);
    setUploadProgress('');
    setObraId(null);
    setJobId(null);
    setJobEstado(null);
    setSugerencias([]);
    setObras([]);
    setGenerosPorObra({});
    setFinalizando(false);
    setResultadoFinal(null);
    setMergeSel([]);
    setMergeDialogOpen(false);
    setMergeKeepLabels(true);
    setMergePrimaryIdx(0);
    uploadSessionRef.current = { key: '', obraId: null, inFlight: false };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);
  useEffect(() => { if (!isOpen) { stopPolling(); resetState(); } }, [isOpen, stopPolling, resetState]);

  const obrasAgrupadas = useMemo(() => groupSugerenciasByObra(sugerencias), [sugerencias]);

  const progresoPct = useMemo(() => {
    if (!jobEstado?.totalPaginas) return 0;
    return Math.round(((jobEstado.progresoPaginas ?? 0) / jobEstado.totalPaginas) * 100);
  }, [jobEstado]);

  /* ─── File handling ─── */

  const addFiles = useCallback((newFiles) => {
    const ACCEPT_RE = /\.(pdf|png|jpe?g|webp)$/i;
    const snapshot = Array.from(newFiles || [])
      .filter((f) => ACCEPT_RE.test(f.name || '') || ACCEPT_RE.test(f.webkitRelativePath || ''))
      .sort((a, b) =>
        String(a.webkitRelativePath || a.name).localeCompare(
          String(b.webkitRelativePath || b.name),
          'es',
          { numeric: true },
        ),
      );
    if (!snapshot.length) return;
    setFiles((prev) => {
      const keyOf = (f) => `${f.webkitRelativePath || f.name}:${f.size}:${f.lastModified}`;
      const existing = new Set(prev.map(keyOf));
      const toAdd = snapshot.filter((f) => !existing.has(keyOf(f)));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  }, []);

  const removeFile = useCallback((idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = e.dataTransfer?.files;
    if (droppedFiles?.length) addFiles(droppedFiles);
  }, [addFiles]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);

  /* ─── Polling ─── */

  const fetchEstado = useCallback(async (oid, jid) => {
    const r = await bandasAPI.getAutoEtiquetadoEstado(oid, jid);
    const data = r.data || {};
    setJobEstado(data.job || null);
    const pag = Array.isArray(data.paginas) ? data.paginas : [];
    setSugerencias(pag);
    return data?.job?.estado;
  }, []);

  const startPolling = useCallback((oid, jid) => {
    stopPolling();
    pollingRef.current = setInterval(() => {
      fetchEstado(oid, jid).then((estado) => {
        if (estado === 'COMPLETADO' || estado === 'FALLIDO' || estado === 'CANCELADO') {
          stopPolling();
        }
      });
    }, 2000);
    fetchEstado(oid, jid);
  }, [fetchEstado, stopPolling]);

  /* ─── Step transitions ─── */

  const iniciarAnalisis = useCallback(async () => {
    if (!files.length) return;
    setStep('uploading');
    setUploadProgress(`Subiendo archivos… (0/${files.length})`);
    setSugerencias([]);
    setJobEstado(null);
    setErrorMsg('');

    try {
      const session = uploadSessionRef.current;
      const filesKey = files
        .map((f) => `${f.webkitRelativePath || f.name}:${f.size}:${f.lastModified}`)
        .join('|');

      let oid = session.key === filesKey && session.obraId ? session.obraId : null;

      if (!oid) {
        if (session.inFlight) return;
        uploadSessionRef.current = { key: filesKey, obraId: null, inFlight: true };
        const res = await bandasAPI.createObraPartitura({ titulo: '_lote_temporal' });
        oid = res.data?.id;
        if (!oid) throw new Error('No se pudo crear la obra temporal');
        uploadSessionRef.current = { key: filesKey, obraId: oid, inFlight: true };
      }

      setObraId(oid);
      const BATCH = 30;
      const avisosTruncado = [];
      for (let i = 0; i < files.length; i += BATCH) {
        setUploadProgress(`Subiendo archivos… (${Math.min(i + BATCH, files.length)}/${files.length})`);
        const up = await bandasAPI.uploadObraPartituraArchivos(oid, files.slice(i, i + BATCH));
        if (up?.data?.avisoTruncado) avisosTruncado.push(up.data.avisoTruncado);
      }
      uploadSessionRef.current = { key: filesKey, obraId: oid, inFlight: false };
      if (avisosTruncado.length > 0) {
        toaster.create({
          title: 'Algunas páginas no se han subido',
          description: avisosTruncado[0],
          type: 'warning',
          duration: 12000,
          closable: true,
        });
      }

      setStep('analyzing');
      const r = await bandasAPI.iniciarAutoEtiquetado(oid, { reusarSiActivo: false });
      const jid = r.data?.jobId;
      if (!jid) throw new Error('No se pudo iniciar el análisis');
      setJobId(jid);
      startPolling(oid, jid);
    } catch (e) {
      setStep('error');
      setErrorMsg(e?.response?.data?.message || e?.message || 'Error al analizar');
    }
  }, [files, startPolling]);

  useEffect(() => {
    if (step !== 'analyzing') return;
    if (!jobEstado) return;
    if (jobEstado.estado === 'COMPLETADO') {
      stopPolling();
      const grouped = groupSugerenciasByObra(sugerencias);
      setObras(grouped);
      setGenerosPorObra({});
      setMergeSel([]);
      setStep('summary');
    }
    if (jobEstado.estado === 'FALLIDO') {
      stopPolling();
      setStep('error');
      setErrorMsg(jobEstado.errorMensaje || 'El análisis automático falló');
    }
  }, [step, jobEstado, sugerencias, stopPolling]);

  const handleFinalize = useCallback(async () => {
    if (finalizando || !obraId || !obras.length) return;
    setFinalizando(true);
    setStep('finalizing');

    try {
      const body = {
        obras: obras.map((g, i) => ({
          titulo: g.titulo || 'Sin título',
          autor: g.autor || undefined,
          generoId: generosPorObra[i] || undefined,
          paginaIds: g.paginas.map((p) => p.paginaId),
        })),
      };

      const res = await bandasAPI.dividirLoteObras(obraId, body);
      const data = res.data;

      if (detectarNuevosMaestros && crearMaestrosPendientes) {
        for (const obra of data.obras || []) {
          try {
            const detRes = await bandasAPI.getObraPartitura(obra.id);
            const detail = detRes.data;
            if (detail) {
              const faltantes = detectarNuevosMaestros(detail);
              if (faltantes.instrumentos?.length || faltantes.voces?.length) {
                await crearMaestrosPendientes(faltantes);
              }
            }
          } catch { /* ignore */ }
        }
      }

      setResultadoFinal(data);
      setStep('done');
    } catch (e) {
      setStep('error');
      setErrorMsg(e?.response?.data?.message || e?.message || 'Error al crear las obras');
    } finally {
      setFinalizando(false);
    }
  }, [finalizando, obraId, obras, generosPorObra, detectarNuevosMaestros, crearMaestrosPendientes]);

  const toggleMergeSel = useCallback((idx, checked) => {
    setMergeSel((prev) => {
      if (checked) return prev.includes(idx) ? prev : [...prev, idx].sort((a, b) => a - b);
      return prev.filter((i) => i !== idx);
    });
  }, []);

  const openMergeDialog = useCallback(() => {
    if (mergeSel.length < 2) return;
    const primary = Math.min(...mergeSel);
    setMergePrimaryIdx(primary);
    setMergeKeepLabels(true);
    setMergeDialogOpen(true);
  }, [mergeSel]);

  const confirmMergeObras = useCallback(() => {
    if (mergeSel.length < 2) return;
    const primary = mergeSel.includes(mergePrimaryIdx)
      ? mergePrimaryIdx
      : Math.min(...mergeSel);
    const insertAt = Math.min(...mergeSel);
    const primaryGenre = generosPorObra[primary];

    setObras((prev) => mergeObrasGroups(prev, mergeSel, primary, mergeKeepLabels));
    setGenerosPorObra((prev) => {
      const next = {};
      let newIdx = 0;
      const len = Object.keys(prev).length || obras.length;
      const total = Math.max(len, obras.length);
      for (let i = 0; i < total; i++) {
        if (mergeSel.includes(i)) {
          if (i === insertAt) {
            if (primaryGenre) next[newIdx] = primaryGenre;
            newIdx += 1;
          }
        } else if (prev[i]) {
          next[newIdx] = prev[i];
          newIdx += 1;
        } else {
          newIdx += 1;
        }
      }
      return next;
    });
    setMergeSel([]);
    setMergeDialogOpen(false);
    toaster.create({
      title: 'Obras unidas',
      description: mergeKeepLabels
        ? 'Se conservó el etiquetado de las páginas.'
        : 'Se quitó el etiquetado; revisa las páginas antes de crear.',
      type: 'success',
      duration: 5000,
      closable: true,
    });
  }, [mergeSel, mergePrimaryIdx, mergeKeepLabels, generosPorObra, obras.length]);

  const handleCerrar = useCallback(() => {
    stopPolling();
    if (obraId && step !== 'done') {
      bandasAPI.deleteObraPartitura(obraId).catch(() => {});
    }
    onClose();
  }, [stopPolling, onClose, obraId, step]);
  const handleCompletado = () => { if (onComplete) onComplete(); stopPolling(); onClose(); };
  const bloquearCierre = step === 'uploading' || step === 'analyzing' || step === 'finalizing';

  /* ─── Warnings computed from analysis ─── */
  const warnings = useMemo(() => {
    if (!obras.length) return { lowConfidence: [], orientation: [], nuevosInstr: [], nuevasVoces: [] };
    const lowConfidence = [];
    const orientation = [];
    for (let oi = 0; oi < obras.length; oi++) {
      for (const s of obras[oi].paginas) {
        const conf = s.confianza ?? 0;
        if (conf < CONF_UMBRAL_ETIQUETAR && !s.errorMensaje) {
          lowConfidence.push({
            obraTitulo: obras[oi].titulo,
            orden: s.orden,
            confianza: conf,
          });
        }
        const motivo = motivoRevisionOrientacion(s);
        if (motivo) {
          orientation.push({ obraTitulo: obras[oi].titulo, orden: s.orden, motivo: motivo.motivo });
        }
      }
    }
    return { lowConfidence, orientation, nuevosInstr: [], nuevasVoces: [] };
  }, [obras]);

  return (
    <>
    <Dialog.Root
      open={isOpen}
      placement='center'
      size="xl"
      closeOnInteractOutside={!bloquearCierre}
      closeOnEscape={!bloquearCierre}
      onOpenChange={e => {
        if (!e.open) {
          (bloquearCierre ? () => {} : handleCerrar)();
        }
      }}
    >
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            mx={4}
            maxH="90vh"
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            <Dialog.Header flexShrink={0}>
              {step === 'upload' && 'Añadir obras'}
              {step === 'uploading' && 'Subiendo archivos…'}
              {step === 'analyzing' && 'Analizando partituras con IA'}
              {step === 'summary' && 'Resumen de obras detectadas'}
              {step === 'genres' && 'Tipo de obra'}
              {step === 'warnings' && 'Revisión antes de crear'}
              {step === 'finalizing' && 'Creando obras…'}
              {step === 'done' && 'Obras creadas'}
              {step === 'error' && 'No se pudo completar'}
            </Dialog.Header>
            {!bloquearCierre && <Dialog.CloseTrigger />}

            <Dialog.Body flex="1" minH={0} overflowY="auto">
              {/* ─── STEP: Upload ─── */}
              {step === 'upload' && (
                <VStack align="stretch" gap={4}>
                  <Alert.Root status="info" borderRadius="md" fontSize="sm">
                    <Alert.Indicator />
                    <Box>
                      <Text>
                        Sube archivos PDF o imágenes de tus partituras. Pueden contener páginas de <strong>varias
                        obras distintas</strong>: la IA las detectará y separará automáticamente.
                      </Text>
                      <Text mt={1} fontWeight="600">
                        Recomendación: no excedas 4–5 obras por subida para obtener los mejores resultados.
                      </Text>
                    </Box>
                  </Alert.Root>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    style={{ display: 'none' }}
                    data-testid="alta-obra-file-input"
                    onChange={(e) => {
                      const snapshot = Array.from(e.target.files || []);
                      e.target.value = '';
                      if (snapshot.length) addFiles(snapshot);
                    }}
                  />
                  <input
                    ref={folderInputRef}
                    type="file"
                    multiple
                    webkitdirectory=""
                    directory=""
                    style={{ display: 'none' }}
                    data-testid="alta-obra-folder-input"
                    onChange={(e) => {
                      const snapshot = Array.from(e.target.files || []);
                      e.target.value = '';
                      if (snapshot.length) addFiles(snapshot);
                    }}
                  />
                  <Box
                    border="2px dashed"
                    borderColor={dropZoneBorder}
                    borderRadius="lg"
                    bg={dropZoneBg}
                    p={8}
                    textAlign="center"
                    cursor="pointer"
                    _hover={{ borderColor: 'purple.400', bg: dropZoneHoverBg }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Icon w={10} h={10} color="purple.400" mb={2} asChild><FaCloudUploadAlt /></Icon>
                    <Text fontSize="sm" fontWeight="600" color="purple.600">
                      Arrastra archivos aquí o haz clic para seleccionar
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      PDF, PNG, JPG, WebP
                    </Text>
                  </Box>
                  <HStack justify="center" gap={3} flexWrap="wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      colorPalette="purple"
                      data-testid="alta-obra-select-folder"
                      onClick={(e) => {
                        e.stopPropagation();
                        folderInputRef.current?.click();
                      }}
                    >
                      Seleccionar carpeta
                    </Button>
                    <Text fontSize="xs" color="gray.500">
                      También puedes seleccionar una carpeta de PDFs
                    </Text>
                  </HStack>

                  {files.length > 0 && (
                    <Box maxH="30vh" overflowY="auto" borderWidth="1px" borderRadius="md" p={2}>
                      <VStack align="stretch" gap={1}>
                        {files.map((f, i) => (
                          <Flex key={`${f.webkitRelativePath || f.name}-${i}`} align="center" gap={2} fontSize="sm" py={1} px={2}
                            _hover={{ bg: fileRowHoverBg }} borderRadius="md">
                            <Icon color="purple.400" flexShrink={0} asChild><FaFileAlt /></Icon>
                            <Text flex={1} lineClamp={1}>{f.webkitRelativePath || f.name}</Text>
                            <Text fontSize="xs" color="gray.500" flexShrink={0}>
                              {(f.size / 1024 / 1024).toFixed(1)} MB
                            </Text>
                            <Button size="xs" variant="ghost" colorPalette="red" onClick={(e) => { e.stopPropagation(); removeFile(i); }}>
                              <FaTrash />
                            </Button>
                          </Flex>
                        ))}
                      </VStack>
                      <Text fontSize="xs" color="gray.500" mt={2} textAlign="right">
                        {files.length} archivo{files.length !== 1 ? 's' : ''} seleccionado{files.length !== 1 ? 's' : ''}
                      </Text>
                    </Box>
                  )}
                </VStack>
              )}

              {/* ─── STEP: Uploading ─── */}
              {step === 'uploading' && (
                <Flex direction="column" align="center" py={8} gap={4}>
                  <Spinner size="lg" color="purple.500" />
                  <Text fontSize="sm" color="gray.600" textAlign="center">{uploadProgress}</Text>
                </Flex>
              )}

              {/* ─── STEP: Analyzing ─── */}
              {step === 'analyzing' && (
                <VStack align="stretch" gap={4}>
                  <HStack gap={2}>
                    <FaRobot color="var(--chakra-colors-purple-500)" />
                    <Text fontSize="sm" fontWeight="600">Progreso del análisis</Text>
                  </HStack>

                  <Box>
                    <Flex justify="space-between" mb={1}>
                      <Text fontSize="sm" color="gray.600">
                        {jobEstado?.progresoPaginas ?? 0} / {jobEstado?.totalPaginas ?? '—'} páginas
                      </Text>
                      <Badge colorPalette="purple">{progresoPct}%</Badge>
                    </Flex>
                    <Progress.Root value={progresoPct} colorPalette="purple" size="md" striped animated>
                      <Progress.Track>
                        <Progress.Range />
                      </Progress.Track>
                    </Progress.Root>
                  </Box>

                  {obrasAgrupadas.length > 0 && (
                    <Box bg="purple.50" _dark={{ bg: 'whiteAlpha.100' }} p={3} borderRadius="md" maxH="28vh" overflowY="auto">
                      <Text fontSize="xs" color="gray.500" mb={2} fontWeight="600">
                        Obras detectadas: {obrasAgrupadas.length}
                      </Text>
                      <VStack align="stretch" gap={2}>
                        {obrasAgrupadas.map((g, i) => {
                          const etiquetadas = g.paginas.filter((p) => !p.errorMensaje && (p.confianza ?? 0) >= CONF_UMBRAL_ETIQUETAR).length;
                          return (
                            <Flex key={i} fontSize="sm" gap={2} align="center">
                              <Badge colorPalette="purple" flexShrink={0}>#{i + 1}</Badge>
                              <Text flex={1} fontWeight="500" lineClamp={1}>
                                {g.titulo || 'Sin título'}
                                {g.autor ? ` — ${g.autor}` : ''}
                              </Text>
                              <Text fontSize="xs" color="gray.500" flexShrink={0}>
                                {g.paginas.length} pág.
                              </Text>
                              <Badge colorPalette={etiquetadas === g.paginas.length ? 'green' : 'yellow'} fontSize="0.65rem">
                                {etiquetadas}/{g.paginas.length} etiq.
                              </Badge>
                            </Flex>
                          );
                        })}
                      </VStack>
                    </Box>
                  )}

                  {sugerencias.length === 0 && (
                    <Flex align="center" gap={2} p={2}>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color="gray.600">Esperando primera página…</Text>
                    </Flex>
                  )}

                  <Box maxH="25vh" overflowY="auto" borderWidth="1px" borderRadius="md" p={2}>
                    <VStack align="stretch" gap={1}>
                      {sugerencias.map((s) => (
                        <Flex key={s.sugerenciaId || s.paginaId} fontSize="sm" gap={2} align="center">
                          <Badge>#{s.orden}</Badge>
                          <Text flex={1} lineClamp={1}>
                            {Array.isArray(s.instrumentosVoces) && s.instrumentosVoces.length
                              ? s.instrumentosVoces
                                  .map((iv) => `${iv.nombre || iv.slug}${iv.voces?.length ? ` (${iv.voces.join(', ')})` : ''}`)
                                  .join(' · ')
                              : s.errorMensaje || (s.paginaContinuacion ? '(continuación)' : 'Analizando…')}
                          </Text>
                          {s.confianza != null && !s.errorMensaje && (
                            <Badge colorPalette={s.confianza >= CONF_UMBRAL_ETIQUETAR ? 'green' : s.confianza >= 0.5 ? 'yellow' : 'red'}>
                              {Math.round(s.confianza * 100)}%
                            </Badge>
                          )}
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                </VStack>
              )}

              {/* ─── STEP: Summary ─── */}
              {step === 'summary' && (
                <VStack align="stretch" gap={4}>
                  <Alert.Root status="success" borderRadius="md" fontSize="sm">
                    <Alert.Indicator />
                    <Text>
                      Se han detectado <strong>{obras.length} obra{obras.length !== 1 ? 's' : ''}</strong> con
                      un total de {sugerencias.length} páginas.
                      {obras.length > 1 ? (
                        <>
                          {' '}Si la IA ha separado de más una misma obra, selecciónalas y únelas.
                        </>
                      ) : null}
                    </Text>
                  </Alert.Root>

                  {obras.length > 1 && (
                    <Flex
                      align={{ base: 'stretch', sm: 'center' }}
                      direction={{ base: 'column', sm: 'row' }}
                      gap={2}
                      justify="space-between"
                    >
                      <Text fontSize="sm" color="gray.600">
                        {mergeSel.length === 0
                          ? 'Selecciona 2 o más obras para unirlas.'
                          : `${mergeSel.length} obra${mergeSel.length !== 1 ? 's' : ''} seleccionada${mergeSel.length !== 1 ? 's' : ''}`}
                      </Text>
                      <HStack gap={2} justify="flex-end">
                        {mergeSel.length > 0 && (
                          <Button size="sm" variant="ghost" onClick={() => setMergeSel([])}>
                            Limpiar
                          </Button>
                        )}
                        <Button
                          size="sm"
                          colorPalette="purple"
                          variant="outline"
                          disabled={mergeSel.length < 2}
                          onClick={openMergeDialog}
                        >
                          <Icon as={FaObjectGroup} />
                          Unir seleccionadas
                        </Button>
                      </HStack>
                    </Flex>
                  )}

                  <VStack align="stretch" gap={3}>
                    {obras.map((g, i) => {
                      const etiquetadas = g.paginas.filter(
                        (p) => !p.errorMensaje && (p.confianza ?? 0) >= CONF_UMBRAL_ETIQUETAR && Array.isArray(p.instrumentosVoces) && p.instrumentosVoces.length,
                      ).length;
                      const selected = mergeSel.includes(i);
                      return (
                        <Box
                          key={`${g.titulo}-${i}-${g.paginas[0]?.paginaId || i}`}
                          borderWidth="1px"
                          borderRadius="md"
                          p={3}
                          borderColor={selected ? 'purple.300' : undefined}
                          bg={selected ? 'purple.50' : undefined}
                          _dark={{ bg: selected ? 'whiteAlpha.100' : undefined }}
                        >
                          <Flex align="flex-start" gap={3} mb={2}>
                            {obras.length > 1 && (
                              <Checkbox.Root
                                mt={0.5}
                                colorPalette="purple"
                                checked={selected}
                                onCheckedChange={(details) =>
                                  toggleMergeSel(i, details.checked === true)
                                }
                              >
                                <Checkbox.HiddenInput />
                                <Checkbox.Control>
                                  <Checkbox.Indicator />
                                </Checkbox.Control>
                              </Checkbox.Root>
                            )}
                            <Box flex={1} minW={0}>
                              <Flex align="center" gap={2} mb={1}>
                                <Badge colorPalette="purple" fontSize="sm">Obra {i + 1}</Badge>
                                <Text fontWeight="600" flex={1} lineClamp={1}>
                                  {g.titulo}
                                </Text>
                              </Flex>
                              {g.autor && (
                                <Text fontSize="sm" color="gray.600" mb={1}>Autor: {g.autor}</Text>
                              )}
                              <HStack gap={3} fontSize="xs" color="gray.500">
                                <Text>{g.paginas.length} página{g.paginas.length !== 1 ? 's' : ''}</Text>
                                <Badge colorPalette={etiquetadas === g.paginas.length ? 'green' : 'yellow'} fontSize="0.65rem">
                                  {etiquetadas} etiquetada{etiquetadas !== 1 ? 's' : ''}
                                </Badge>
                              </HStack>
                            </Box>
                          </Flex>
                          <Box
                            mt={2}
                            maxH="140px"
                            overflowY="auto"
                            borderWidth="1px"
                            borderRadius="md"
                            borderColor="blackAlpha.100"
                            _dark={{ borderColor: 'whiteAlpha.200' }}
                            px={2}
                            py={1}
                          >
                            <VStack align="stretch" gap={0}>
                              {g.paginas.map((p) => (
                                <Flex key={p.paginaId} fontSize="xs" gap={2} align="center" py={0.5}>
                                  <Text color="gray.400" w="40px" flexShrink={0}>p.{p.orden}</Text>
                                  <Text flex={1} lineClamp={1} color="gray.600">
                                    {Array.isArray(p.instrumentosVoces) && p.instrumentosVoces.length
                                      ? p.instrumentosVoces.map((iv) => iv.nombre || iv.slug).join(', ')
                                      : p.paginaContinuacion ? '(cont.)' : '—'}
                                  </Text>
                                  {p.confianza != null && (
                                    <Badge size="sm" colorPalette={p.confianza >= CONF_UMBRAL_ETIQUETAR ? 'green' : p.confianza >= 0.5 ? 'yellow' : 'red'} fontSize="0.6rem">
                                      {Math.round(p.confianza * 100)}%
                                    </Badge>
                                  )}
                                </Flex>
                              ))}
                            </VStack>
                          </Box>
                        </Box>
                      );
                    })}
                  </VStack>
                </VStack>
              )}

              {/* ─── STEP: Genres ─── */}
              {step === 'genres' && (
                <VStack align="stretch" gap={4}>
                  <Text fontSize="sm" color="gray.600">
                    Asigna un tipo (género) a cada obra detectada. Es opcional; puedes dejarlo sin especificar.
                  </Text>
                  {obras.map((g, i) => (
                    <Field.Root key={i}>
                      <Field.Label fontSize="sm">
                        <Badge colorPalette="purple" mr={2}>Obra {i + 1}</Badge>
                        {g.titulo}
                        {g.autor ? ` — ${g.autor}` : ''}
                      </Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          size="sm"
                          placeholder="Sin especificar"
                          value={generosPorObra[i] || ''}
                          onChange={(e) => setGenerosPorObra((prev) => ({ ...prev, [i]: e.target.value }))}>
                          {generosCatalog.map((gen) => (
                            <option key={gen.id} value={gen.id}>{gen.nombre}</option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                  ))}
                </VStack>
              )}

              {/* ─── STEP: Warnings ─── */}
              {step === 'warnings' && (
                <VStack align="stretch" gap={4}>
                  <Alert.Root status="info" borderRadius="md" fontSize="sm">
                    <Alert.Indicator />
                    <Text>
                      Se crearán <strong>{obras.length} obra{obras.length !== 1 ? 's' : ''}</strong> en estado
                      borrador. Revisa los avisos antes de continuar.
                    </Text>
                  </Alert.Root>

                  {warnings.lowConfidence.length > 0 && (
                    <Alert.Root
                      status="warning"
                      borderRadius="md"
                      variant='subtle'
                      borderStartWidth='3px'
                      borderStartColor='colorPalette.solid'>
                      <Alert.Indicator asChild><FaExclamationTriangle /></Alert.Indicator>
                      <Box flex="1" minW={0}>
                        <Text fontWeight="600" fontSize="sm">Páginas con baja confianza</Text>
                        <Text fontSize="xs" color="gray.600" mb={2}>
                          Estas páginas no se etiquetarán automáticamente. Revísalas en el editor de cada obra.
                        </Text>
                        <Box maxH="180px" overflowY="auto">
                          <List.Root fontSize="xs" gap={1}>
                            {warnings.lowConfidence.map((w, i) => (
                              <List.Item key={i}>
                                <strong>{w.obraTitulo}</strong> — Página {w.orden}: confianza {Math.round(w.confianza * 100)}%
                              </List.Item>
                            ))}
                          </List.Root>
                        </Box>
                      </Box>
                    </Alert.Root>
                  )}

                  {warnings.orientation.length > 0 && (
                    <Alert.Root
                      status="warning"
                      borderRadius="md"
                      variant='subtle'
                      borderStartWidth='3px'
                      borderStartColor='colorPalette.solid'>
                      <Alert.Indicator />
                      <Box flex="1" minW={0}>
                        <Text fontWeight="600" fontSize="sm">Revisa la orientación</Text>
                        <Box maxH="180px" overflowY="auto" mt={1}>
                          <List.Root fontSize="xs" gap={1}>
                            {warnings.orientation.map((w, i) => (
                              <List.Item key={i}>
                                <strong>{w.obraTitulo}</strong> — Página {w.orden}: {w.motivo}
                              </List.Item>
                            ))}
                          </List.Root>
                        </Box>
                      </Box>
                    </Alert.Root>
                  )}

                  {warnings.lowConfidence.length === 0 && warnings.orientation.length === 0 && (
                    <Alert.Root status="success" borderRadius="md" fontSize="sm">
                      <Alert.Indicator />
                      <Text>Todo correcto. Todas las páginas tienen buena confianza y orientación.</Text>
                    </Alert.Root>
                  )}
                </VStack>
              )}

              {/* ─── STEP: Finalizing ─── */}
              {step === 'finalizing' && (
                <Flex direction="column" align="center" py={8} gap={4}>
                  <Spinner size="lg" color="purple.500" />
                  <Text fontSize="sm" color="gray.600">Creando {obras.length} obra{obras.length !== 1 ? 's' : ''} y aplicando etiquetas…</Text>
                </Flex>
              )}

              {/* ─── STEP: Done ─── */}
              {step === 'done' && resultadoFinal && (
                <VStack align="stretch" gap={4}>
                  <Alert.Root status="success" borderRadius="md">
                    <Alert.Indicator asChild><FaCheckCircle /></Alert.Indicator>
                    <Box>
                      <Text fontWeight="600">
                        {resultadoFinal.obras?.length || 0} obra{(resultadoFinal.obras?.length || 0) !== 1 ? 's' : ''} creada{(resultadoFinal.obras?.length || 0) !== 1 ? 's' : ''} en borrador
                      </Text>
                      <VStack align="stretch" gap={1} mt={2} maxH="180px" overflowY="auto">
                        {resultadoFinal.obras?.map((o) => (
                          <Text key={o.id} fontSize="sm">
                            <strong>{o.titulo}</strong>{o.autor ? ` — ${o.autor}` : ''} ({o.totalPaginas} pág.)
                          </Text>
                        ))}
                      </VStack>
                      <Text fontSize="sm" color="gray.600" mt={2}>
                        Revisa cada obra en el listado. Cuando esté correcta, pulsa «Pasar a listo».
                      </Text>
                    </Box>
                  </Alert.Root>

                  {resultadoFinal.warnings?.length > 0 && (
                    <Alert.Root status="warning" borderRadius="md">
                      <Alert.Indicator />
                      <Box flex="1">
                        <Text fontWeight="600" fontSize="sm">Páginas por revisar</Text>
                        <Box maxH="160px" overflowY="auto" mt={1}>
                          <List.Root fontSize="xs" gap={1}>
                            {resultadoFinal.warnings.map((w, i) => (
                              <List.Item key={i}>
                                Obra {w.obraIndex + 1}, pág. {w.paginaOrden}: {w.motivo}
                              </List.Item>
                            ))}
                          </List.Root>
                        </Box>
                      </Box>
                    </Alert.Root>
                  )}

                  {(resultadoFinal.nuevosInstrumentos?.length > 0 || resultadoFinal.nuevasVoces?.length > 0) && (
                    <Alert.Root status="info" borderRadius="md">
                      <Alert.Indicator />
                      <Box flex="1">
                        <Text fontWeight="600" fontSize="sm">Nuevos instrumentos o voces detectados</Text>
                        {resultadoFinal.nuevosInstrumentos?.length > 0 && (
                          <Text fontSize="xs" mt={1}>
                            Instrumentos: {resultadoFinal.nuevosInstrumentos.join(', ')}
                          </Text>
                        )}
                        {resultadoFinal.nuevasVoces?.length > 0 && (
                          <Text fontSize="xs" mt={1}>
                            Voces: {resultadoFinal.nuevasVoces.join(', ')}
                          </Text>
                        )}
                      </Box>
                    </Alert.Root>
                  )}
                </VStack>
              )}

              {/* ─── STEP: Error ─── */}
              {step === 'error' && (
                <Alert.Root status="error" borderRadius="md">
                  <Alert.Indicator />
                  <Text fontSize="sm">{errorMsg || 'Error desconocido'}</Text>
                </Alert.Root>
              )}
            </Dialog.Body>

            <Dialog.Footer flexShrink={0}>
              {step === 'upload' && (
                <>
                  <Button variant="ghost" mr={3} onClick={handleCerrar}>Cancelar</Button>
                  <Button colorPalette="purple" disabled={!files.length} onClick={iniciarAnalisis}>
                    Siguiente
                  </Button>
                </>
              )}
              {step === 'summary' && (
                <>
                  <Button variant="ghost" mr={3} onClick={handleCerrar}>Cancelar</Button>
                  <Button colorPalette="purple" onClick={() => setStep('genres')}>Siguiente</Button>
                </>
              )}
              {step === 'genres' && (
                <>
                  <Button variant="ghost" mr={3} onClick={() => setStep('summary')}>Atrás</Button>
                  <Button colorPalette="purple" onClick={() => setStep('warnings')}>Siguiente</Button>
                </>
              )}
              {step === 'warnings' && (
                <>
                  <Button variant="ghost" mr={3} onClick={() => setStep('genres')}>Atrás</Button>
                  <Button colorPalette="purple" onClick={handleFinalize} loading={finalizando}>
                    Crear obras
                  </Button>
                </>
              )}
              {step === 'done' && (
                <Button colorPalette="purple" onClick={handleCompletado}>Cerrar</Button>
              )}
              {step === 'error' && (
                <Button variant="ghost" onClick={handleCerrar}>Cerrar</Button>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>

    <Dialog.Root
      open={mergeDialogOpen}
      placement="center"
      size="md"
      onOpenChange={(e) => {
        if (!e.open) setMergeDialogOpen(false);
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Unir obras</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="stretch" gap={4}>
                <Text fontSize="sm" color="gray.600">
                  Vas a unir <strong>{mergeSel.length} obras</strong> en una sola.
                  Las páginas se ordenarán por número de página.
                </Text>

                <Field.Root>
                  <Field.Label fontSize="sm">Título y autor de la obra resultante</Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      size="sm"
                      value={String(mergePrimaryIdx)}
                      onChange={(e) => setMergePrimaryIdx(Number(e.target.value))}
                    >
                      {mergeSel.map((idx) => (
                        <option key={idx} value={idx}>
                          Obra {idx + 1}: {obras[idx]?.titulo || 'Sin título'}
                          {obras[idx]?.autor ? ` — ${obras[idx].autor}` : ''}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>

                <Box borderWidth="1px" borderRadius="md" p={3}>
                  <Text fontSize="sm" fontWeight="600" mb={2}>
                    ¿Conservar el etiquetado?
                  </Text>
                  <Text fontSize="xs" color="gray.600" mb={3}>
                    Instrumentos, voces y confianza detectados por la IA en cada página.
                  </Text>
                  <VStack align="stretch" gap={2}>
                    <Box
                      as="button"
                      type="button"
                      textAlign="left"
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      borderColor={mergeKeepLabels ? 'purple.400' : undefined}
                      bg={mergeKeepLabels ? 'purple.50' : undefined}
                      _dark={{ bg: mergeKeepLabels ? 'whiteAlpha.100' : undefined }}
                      onClick={() => setMergeKeepLabels(true)}
                    >
                      <Text fontSize="sm" fontWeight="600">Sí, conservar etiquetas</Text>
                      <Text fontSize="xs" color="gray.600">
                        Se mantienen instrumentos y voces de cada página.
                      </Text>
                    </Box>
                    <Box
                      as="button"
                      type="button"
                      textAlign="left"
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      borderColor={!mergeKeepLabels ? 'purple.400' : undefined}
                      bg={!mergeKeepLabels ? 'purple.50' : undefined}
                      _dark={{ bg: !mergeKeepLabels ? 'whiteAlpha.100' : undefined }}
                      onClick={() => setMergeKeepLabels(false)}
                    >
                      <Text fontSize="sm" fontWeight="600">No, quitar etiquetas</Text>
                      <Text fontSize="xs" color="gray.600">
                        Las páginas quedarán sin etiquetar para revisarlas después.
                      </Text>
                    </Box>
                  </VStack>
                </Box>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={() => setMergeDialogOpen(false)}>
                Cancelar
              </Button>
              <Button colorPalette="purple" onClick={confirmMergeObras}>
                Unir obras
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
    </>
  );
}
